import { normalizeMatchableName } from "@common/helpers/string";
import { Entity } from "@common/types/entity";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import {
    getAccrualExtraction,
    resolveAccrualRowExtraction,
} from "./accrualImporter.helpers";
import { getExtractionOrderIndex } from "./useAccrualImporterCounterpartyEditor.helpers";

type WorkspaceEntity = Pick<Entity, "name" | "isBusiness" | "isMockEntity">;

function parsedOwnPartyNames({
    financialDocument,
    extractionId,
    mode,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId?: string;
    mode: AccrualDocumentImporterMode;
}): string[] {
    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        extractionId,
    );
    if (!extraction) {
        return [];
    }

    const orderIndex = getExtractionOrderIndex({
        financialDocument,
        extractionId: extraction.id,
        mode,
    });
    if (orderIndex == null) {
        return [];
    }

    const container = getAccrualExtraction(financialDocument);
    if (!container || !("originalExtraction" in container)) {
        return [];
    }

    const document = container.originalExtraction?.documents[orderIndex];
    const party = mode === "invoices" ? document?.issuer : document?.recipient;
    return (party?.names ?? [])
        .map((name) => name.trim())
        .filter((name) => name.length > 0);
}

export function getUnmatchedParsedEntityName({
    financialDocument,
    extractionId,
    mode,
    entities,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId?: string;
    mode: AccrualDocumentImporterMode;
    entities: WorkspaceEntity[] | undefined;
}): string | null {
    if (!entities) {
        return null;
    }
    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        extractionId,
    );
    if (!extraction || extraction.entityId != null) {
        return null;
    }

    const names = parsedOwnPartyNames({
        financialDocument,
        extractionId,
        mode,
    });
    if (names.length === 0) {
        return null;
    }

    const workspaceNames = new Set(
        entities.flatMap((entity) => {
            if (!entity.isBusiness || entity.isMockEntity || !entity.name) {
                return [];
            }
            return [normalizeMatchableName(entity.name)];
        }),
    );
    const hasWorkspaceMatch = names.some((name) =>
        workspaceNames.has(normalizeMatchableName(name)),
    );
    if (hasWorkspaceMatch) {
        return null;
    }

    return names[0];
}

export function getSuggestedOwnPartyEntity<T extends WorkspaceEntity>({
    financialDocument,
    extractionId,
    mode,
    entities,
}: {
    financialDocument: FinancialDocumentExtended;
    extractionId?: string;
    mode: AccrualDocumentImporterMode;
    entities: T[];
}): T | undefined {
    const names = new Set(
        parsedOwnPartyNames({ financialDocument, extractionId, mode }).map(
            normalizeMatchableName,
        ),
    );
    const matches = entities.filter(
        (entity) =>
            entity.isBusiness &&
            !entity.isMockEntity &&
            entity.name &&
            names.has(normalizeMatchableName(entity.name)),
    );
    return matches.length === 1 ? matches[0] : undefined;
}

export function unmatchedEntityReviewTaskText(name: string): string {
    return `"${name}" is not in this workspace. Select an entity.`;
}
